import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";

interface TermsModalProps {
  open: boolean;
  onClose: () => void;
}

const TermsModal: React.FC<TermsModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();

  // obtém as seções do JSON de i18n
  const sections = t("login.termsModal.sections", {
    returnObjects: true,
  }) as Array<{
    header: string;
    content: string;
  }>;

  const contentRef = useRef<HTMLDivElement>(null);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);

  // ao abrir, reseta o estado e rola pro topo
  useEffect(() => {
    if (open) {
      setScrolledToEnd(false);
      if (contentRef.current) contentRef.current.scrollTop = 0;
    }
  }, [open]);

  // verifica se chegou ao fim do scroll
  const handleScroll = () => {
    const el = contentRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight) {
      setScrolledToEnd(true);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => scrolledToEnd && onClose()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ m: 0, p: 2, position: "relative" }}>
        {t("login.termsModal.title")}
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={!scrolledToEnd}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box
          ref={contentRef}
          onScroll={handleScroll}
          sx={{ maxHeight: "60vh", overflowY: "auto", px: 1 }}
        >
          {sections.map((sec, i) => (
            <React.Fragment key={i}>
              <Typography variant="h6" gutterBottom>
                {sec.header}
              </Typography>
              <Typography
                paragraph
                variant="body2"
                sx={{ whiteSpace: "pre-line" }}
              >
                {sec.content}
              </Typography>
              {i < sections.length - 1 && <Divider sx={{ my: 2 }} />}
            </React.Fragment>
          ))}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={!scrolledToEnd}>
          {t("login.termsModal.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsModal;
